import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input, output } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoService } from '@ngneat/transloco';
import { HD_CIRCUITRY_NAME_KEY } from '../basic-properties/hd-chart-properties.constants';
import { HD_BODYGRAPH_CIRCUIT_THEME_ICONS } from '../hd-bodygraph-circuit-metadata';
import { getHdBodygraphChannel } from '../hd-bodygraph-metadata';
import { HdBodygraphActivation, HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import {
  HD_CREATIVE_CHANNEL_KEYS,
  hdChannelTypeFor,
  isHdManifestingGeneratorChannel,
} from '../hd-channel-type-metadata';
import { HdChartHoverHighlightService } from '../hd-chart-hover-highlight.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdCycleChannelGateVm } from '../hd-cycle-keynotes.builder';
import { HD_CYCLE_MAIN_KEYNOTES, HdCycleMainKeynoteKey } from '../hd-cycles-cheat-sheet-metadata';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import {
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON,
  getHdPlanetDisplayConfigByPlanetId,
} from '../planet-grid/hd-planet-display-config';
import {
  HdCycleGateKeynoteLineComponent,
  HdCycleGateLineInfoRequest,
} from './hd-cycle-gate-keynote-line.component';
import { HdCycleCardsToggleComponent } from '../advanced-properties/hd-cycle-cards-toggle.component';
import { HdActivePaletteService } from '../hd-active-palette.service';

const P = 'humanDesign.properties';
const ALL_CHANNELS = 'humanDesign.chart.channelInfo.allChannels';

/** Volgorde-index per planetId, conform de planetengrid (zelfde regel als het kanaal-info-paneel). */
const PLANET_ORDER: ReadonlyMap<number, number> = new Map(
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON.map((config, index) => [config.planetId, index]),
);

/** Aanvraag om het gate-info-paneel te openen (zelfde shape als de chart-klik). */
export type HdChannelListGateInfoRequest = HdCycleGateLineInfoRequest;

/** Eén deel van de chips-notitie: tekst en/of glyphs, optioneel in een chart-kleur. */
export interface HdChannelAnnotationPart {
  readonly text?: string;
  readonly glyphs?: readonly string[];
  readonly color?: string | null;
}

/** Aanvraag om het kanaal-info-paneel te openen (zelfde shape als de chart-klik). */
export interface HdChannelListChannelInfoRequest {
  channelKeys: readonly string[];
  gates: readonly number[];
  anchorGate: number;
  origin: { x: number; y: number };
}

interface ChannelTextVm {
  readonly name: string;
  readonly descriptionPrefix: string;
  readonly shortDescription: string;
  readonly descriptionSuffix: string;
}

/** Welke kant(en) een poort van het kanaal activeren; `none` = geen activatie bekend. */
type ChannelGateSource = 'personality' | 'design' | 'both' | 'none';

interface ChannelCardGlyphVm {
  readonly char: string;
  readonly side: 'personality' | 'design';
  /** Planeetnaam + poort.lijn, bv. "Zon 34.2". */
  readonly title: string;
}

interface ChannelCardGateVm {
  readonly gate: number;
  readonly source: ChannelGateSource;
  /** De twee poort-keynotes ("Kracht · Macht"), als tooltip op de badge. */
  readonly tooltip: string;
  readonly glyphs: readonly ChannelCardGlyphVm[];
}

interface ChannelCardCenterVm {
  readonly name: string;
  readonly color: string;
}

interface ChannelCardVm {
  /** Herkenningskleur van het circuit (zoals op de cheat sheets); `null` zonder circuit. */
  readonly circuitColor: string | null;
  readonly circuitIcon: string | null;
  readonly circuitName: string;
  /** Circuitnaam + thema-keynote, als tooltip op het icoon en de naam. */
  readonly circuitTooltip: string;
  readonly centers: readonly ChannelCardCenterVm[];
  readonly typeLabel: string;
  readonly typeHint: string;
  /** Extra kenmerken uit Channels by Type (Manifesting Generator, Creatief). */
  readonly badges: readonly string[];
  readonly gates: readonly ChannelCardGateVm[];
}

/**
 * Eén kanaal in een kanalen-opsomming, als herbruikbaar item.
 *
 * Basis-modus (Basis-paneel, Gedefinieerde Kanalen): een kanaalkaartje in de stijl
 * van de eigenschap-tegels en mini-kaartjes ernaast. Links het circuit-thema-icoon
 * in de circuitkleur; regel 1 de kanaalnaam met rechts een mini-kanaaltje (de twee
 * poort-badges in de kleur van de activerende kant: personality donker, design
 * rood, beide = verloop, met de planeetglyphs naar buiten); regel 2 "Een design
 * van"; regel 3 circuit, de twee verbonden centers en het kanaaltype. Een klik op
 * het kaartje of de naam opent het kanaal-info-paneel (zoals een klik op het
 * kanaal in de chart), een poort-badge het gate-info-paneel.
 *
 * Met {@link cycleGates} gezet toont het item het cycle-scenario over twee
 * regels: regel 1 links "Het kanaal van: X", rechts de twee poort-chips
 * ({@link HdCycleGateKeynoteLineComponent} — natal-chip met glyph, cycle-chip
 * met glyphs + keynotes); regel 2 "Een design van: Y".
 */
@Component({
  selector: 'hd-channel-list-item',
  templateUrl: './hd-channel-list-item.component.html',
  styleUrl: './hd-channel-list-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgTemplateOutlet, HdCycleGateKeynoteLineComponent, HdCycleCardsToggleComponent],
})
export class HdChannelListItemComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly centerColors = inject(HdActivePaletteService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly hoverHighlight = inject(HdChartHoverHighlightService, { optional: true });

  /** Canonieke kanaal-key uit `HD_BODYGRAPH_CHANNELS` (bv. `'64-47'`). */
  readonly channelKey = input.required<string>();
  /**
   * Basis-modus: de activaties van de chart (configuratie), bron van de kant per
   * poort in het mini-kanaaltje. Ook charts zonder planeetdata (handmatig) kleuren zo.
   */
  readonly activations = input<readonly HdBodygraphActivation[]>([]);
  /** Basis-modus: de planeet-activaties, voor de glyphs naast de poort-badges. */
  readonly planetaryActivations = input<readonly HdBodygraphPlanetaryActivation[]>([]);
  /**
   * Basis-modus: gekoppeld aan de chart-hover (instellingenpaneel-setting, zoals de
   * center- en activatie-kaartjes): hover licht de twee poorten op in de chart,
   * Ctrl/Cmd+klik bevriest ze. Uit = enkel een eenvoudige hover en klik → info.
   */
  readonly chartLinked = input(false, { transform: booleanAttribute });
  /** Cycle-modus: de twee poort-kanten uit de cycle-keynotes-builder. */
  readonly cycleGates = input<readonly [HdCycleChannelGateVm, HdCycleChannelGateVm] | null>(null);
  /**
   * Main keynote van dit kanaal (Persoonlijk/Educatief) — het blokje draagt dan
   * links het cheat-sheet-icoon en een linkerborder in de kaartkleur, zodat de
   * keynote ook zonder de sectiegroepering per kanaal herkenbaar blijft.
   */
  readonly keynoteKey = input<HdCycleMainKeynoteKey | null>(null);
  /**
   * Directe accent-override (composite-blokken: Electromagnetic/Compromis/
   * Dominantie): kleur + icoon zonder cheat-sheet-keynote; wint van keynoteKey.
   */
  readonly accent = input<{ color: string; faIcon: string } | null>(null);
  /**
   * Vrije notitie achter de poort-chips (cycle-modus), opgebouwd uit delen zodat
   * namen in de chart-kleur en planeetglyphs mee kunnen — bv. de
   * compromis-richting "Steven ♂ > compromitteert > Anke's 57 ♀".
   */
  readonly annotation = input<readonly HdChannelAnnotationPart[] | null>(null);
  /**
   * Composite: toon op een both-poort óók de natal-bron-glyphs (chart 1, blauw)
   * naast de partner-glyphs — beide personen activeren die poort.
   */
  readonly showBothSourceGlyphs = input(false);
  /**
   * Cycle-modus: uitklapknop rechts op het blokje. De ouder rendert de I Tjing-
   * kaarten (`hd-cycle-cards`) eronder en bezit de uitklapstaat.
   */
  readonly expandable = input(false, { transform: booleanAttribute });
  readonly expanded = input(false);
  readonly expandToggled = output<void>();

  readonly gateInfoRequested = output<HdChannelListGateInfoRequest>();
  readonly channelInfoRequested = output<HdChannelListChannelInfoRequest>();

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  /** De twee poortnummers, uit de key (behoudt de canonieke volgorde). */
  protected readonly gates = computed<readonly number[]>(() =>
    this.channelKey().split('-').map(Number),
  );

  protected readonly isCycleMode = computed(() => this.cycleGates() != null);

  /** Kleur + icoon van het blokje: accent-override of de cheat-sheet-keynote; `null` zonder beide. */
  protected readonly keynoteMeta = computed<{ color: string; faIcon: string } | null>(() => {
    const accent = this.accent();
    if (accent != null) {
      return accent;
    }
    const key = this.keynoteKey();
    if (key == null) {
      return null;
    }
    return HD_CYCLE_MAIN_KEYNOTES.find(candidate => candidate.key === key) ?? null;
  });

  protected readonly text = computed<ChannelTextVm>(() => {
    const lang = this.lang();
    const translation = this.gateInfo.channelTranslationIn(this.channelKey(), lang);
    const descriptionParts = this.splitLabelAroundPlaceholder(
      `${P}.channelDescriptionLabel`,
      'shortDescription',
      lang,
    );
    return {
      name: translation?.name ?? this.channelKey(),
      descriptionPrefix: descriptionParts.prefix,
      shortDescription: translation?.shortDescription ?? '',
      descriptionSuffix: descriptionParts.suffix,
    };
  });

  /** Basis-modus: alles wat het kanaalkaartje naast naam en design toont. */
  protected readonly card = computed<ChannelCardVm>(() => {
    const lang = this.lang();
    const key = this.channelKey();
    const t = (translationKey: string) => this.transloco.translate<string>(translationKey, undefined, lang);

    const circuit = this.gateInfo.channelMainCircuit(key);
    const circuitNameKey = circuit ? HD_CIRCUITRY_NAME_KEY[circuit.key] : undefined;
    const circuitName = circuitNameKey ? t(`${P}.circuitryNames.${circuitNameKey}`) : '';
    const circuitKeynote = circuit?.theme ? t(`${P}.circuitryKeynotes.${circuit.theme}`) : '';

    const definition = getHdBodygraphChannel(key);
    const type = definition ? hdChannelTypeFor(definition) : null;

    return {
      circuitColor: circuit?.color ?? null,
      circuitIcon: circuit?.theme ? HD_BODYGRAPH_CIRCUIT_THEME_ICONS[circuit.theme] : null,
      circuitName,
      circuitTooltip: [circuitName, circuitKeynote].filter(Boolean).join(' · '),
      centers: (definition?.centers ?? []).map(center => ({
        name: t(`${P}.centerNames.${center}`),
        color: this.centerColors.color(center),
      })),
      typeLabel: type ? t(`${P}.channelTypes.${type}`) : '',
      typeHint: type ? t(`${ALL_CHANNELS}.groups.${type}.hint`) : '',
      badges: [
        ...(isHdManifestingGeneratorChannel(key) ? [t(`${ALL_CHANNELS}.badges.mg`)] : []),
        ...(HD_CREATIVE_CHANNEL_KEYS.has(key) ? [t(`${ALL_CHANNELS}.badges.creative`)] : []),
      ],
      gates: this.gates().map(gate => this.cardGate(gate, lang)),
    };
  });

  /** De poort-chips voor regel 1 rechts (cycle-modus), in kanaal-volgorde. */
  protected readonly chipSides = computed<readonly HdCycleChannelGateVm[]>(() => this.cycleGates() ?? []);

  protected onGateClick(gate: number, event: MouseEvent): void {
    // Binnen het kaartje: de badge opent de poort, niet (ook) het kanaal.
    event.stopPropagation();
    this.gateInfoRequested.emit({ gate, origin: { x: event.clientX, y: event.clientY } });
  }

  protected onChannelClick(event: MouseEvent): void {
    // Kaartje (basis-modus): de naam-knop en het kaartje zelf delen deze handler.
    event.stopPropagation();
    if (this.chartLinked() && (event.ctrlKey || event.metaKey)) {
      this.hoverHighlight?.toggleFreezeForGates(this.gates());
      return;
    }
    const gates = this.gates();
    this.channelInfoRequested.emit({
      channelKeys: [this.channelKey()],
      gates,
      anchorGate: gates[0],
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  /** Kaartje-hover → de twee poorten oplichten in de chart (enkel met de chart-koppeling). */
  protected onCardEnter(): void {
    if (!this.chartLinked()) return;
    this.hoverHighlight?.setGatesHover(this.gates(), 'chart-gate');
  }

  protected onCardLeave(): void {
    if (!this.chartLinked()) return;
    this.hoverHighlight?.setGatesHover(null, 'chart-gate');
  }

  /** Eén poort van het mini-kanaaltje: kant(en), keynote-tooltip en glyphs (personality eerst). */
  private cardGate(gate: number, lang: string): ChannelCardGateVm {
    const sides = new Set(
      this.activations()
        .filter(activation => activation.gate === gate && activation.side !== 'highlight')
        .map(activation => activation.side),
    );
    const source: ChannelGateSource = sides.has('personality')
      ? (sides.has('design') ? 'both' : 'personality')
      : (sides.has('design') ? 'design' : 'none');

    const glyphs = this.planetaryActivations()
      .filter(activation => activation.gate === gate && activation.side !== 'highlight')
      .sort((left, right) => {
        if (left.side !== right.side) {
          return left.side === 'personality' ? -1 : 1;
        }
        return (PLANET_ORDER.get(left.planetId) ?? 99) - (PLANET_ORDER.get(right.planetId) ?? 99);
      })
      .flatMap<ChannelCardGlyphVm>(activation => {
        const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
        if (!config) return [];
        const planetName = this.planetInfo.planetInfoIn(config.key, lang)?.name ?? config.key;
        return [{
          char: config.astronomiconChar,
          side: activation.side === 'design' ? 'design' : 'personality',
          title: `${planetName} ${gate}.${activation.line}`,
        }];
      });

    const keynotes = this.gateInfo.gateInfoIn(gate, lang).translations;
    return {
      gate,
      source,
      tooltip: [keynotes.keynote1, keynotes.keynote2].filter(Boolean).join(' · '),
      glyphs,
    };
  }

  /**
   * Splits een vertaalde label rond een `{{placeholder}}` zodat de waarde apart
   * opgemaakt kan worden (vet) — zelfde aanpak als voorheen in
   * `HdBasicPropertiesComponent`.
   */
  private splitLabelAroundPlaceholder(
    key: string,
    placeholder: string,
    lang: string,
  ): { prefix: string; suffix: string } {
    const marker = '\u0001';
    const translated = this.transloco.translate(key, { [placeholder]: marker }, lang);
    const markerIndex = translated.indexOf(marker);
    if (markerIndex === -1) {
      return { prefix: translated, suffix: '' };
    }
    return {
      prefix: translated.slice(0, markerIndex),
      suffix: translated.slice(markerIndex + marker.length),
    };
  }
}
